import {
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormType } from 'src/app/core';
import { MultiStepFormStep } from './multi-step-form-step.class';

/**
 * MultiStepFormComponent
 *
 * The wizard component is mainly used to pass the entity object,
 * the translation key used in the wizard and to write reusable functions.
 *
 * @example
 * In wizard template:
 * ```
 * <app-multi-step-form
 *  #multiStepForm
 *  [entity]="entity_variable_here"
 *  [isEditMode]="isEditMode"
 *  [MULTI_STEP_FORM_PREFIX]="TRANSLATION_PREFIX_HERE"
 *  [formType]="FormType.FORM_TYPE_HERE"
 *  (onCancel)="cancel()"
 *  (onFormSubmit)="submit($event)"
 * >
 *  <app-wizard-sidebar
 *    sidebar // <=== No "#" in front of the selector's name (required)
 *    [multiStepForm]="multiStepForm" // <== Required to have access to the parent
 *    [activeStepIndex]="multiStepForm.activeStepIndex"
 *    (onActiveStepIndexChange)="multiStepForm.activeStepIndex = $event"
 *  ></app-wizard-sidebar>
 *  <app-step-one-form
 *    #step // <=== Step selector (required)
 *    [multiStepForm]="multiStepForm" // <== Required to have access to the parent
 *    (onStepFormChange)="stepFormChange($event)"
 *  ></app-step-one-form>
 *  <app-step-two-form
 *    #step // <=== Step selector (required)
 *    [multiStepForm]="multiStepForm" // <== Required to have access to the parent
 *  ></app-step-two-form>
 *  <app-step-three-form
 *    #step // <=== Step selector (required)
 *    [multiStepForm]="multiStepForm" // <== Required to have access to the parent
 *    [custom_instance]="instance_variable_here"
 *    (onStepFormChange)="stepFormChange($event)"
 *  ></app-step-three-form>
 *  <app-wizard-summary
 *    #summary // <=== Summary selector (required)
 *    [multiStepForm]="multiStepForm" // <== Required to have access to the parent
 *    [custom_instance]="instance_variable_here"
 *    (onActiveStepIndexChange)="multiStepForm.activeStepIndex = $event"
 *  ></app-wizard-summary>
 * </app-multi-step-form>
 * ```
 *
 * Each step (including summary) used in the template must inherit `MultiStepFormStep`.
 * @example `TestStepComponent extends MultiStepFormStep`
 */
@Component({
  selector: 'app-multi-step-form',
  templateUrl: './multi-step-form.component.html',
  styleUrls: ['./multi-step-form.component.less'],
})
export class MultiStepFormComponent implements OnInit, AfterContentInit {
  @Input() entity: { [key: string]: any } = {};
  @Input() isEditMode: boolean = false;
  @Input() MULTI_STEP_FORM_PREFIX: string;
  @Input() formType: FormType;
  @ViewChild('stepForm') stepFormEl: ElementRef<any>;
  @ContentChildren('step', { descendants: true })
  steps: QueryList<MultiStepFormStep>;
  @ContentChild('summary') summary: MultiStepFormStep;
  wizardSteps: QueryList<MultiStepFormStep> =
    new QueryList<MultiStepFormStep>();
  masterForm: FormGroup[] = [];
  formData: { [key: string]: any };
  isLoading: boolean = false;
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() onFormSubmit: EventEmitter<{ [key: string]: any }> =
    new EventEmitter<{ [key: string]: any }>();

  // In case another step needs its form populated from previous step(s) form(s) values
  private _formDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public multiStepFormFormData: Observable<any> =
    this._formDataSubject.asObservable();

  private _activeStepIndex: number = 0;

  get isMasterFormValid(): boolean {
    return this.masterForm.every((stepForm) => !stepForm.invalid);
  }

  get activeStepForm(): AbstractControl {
    return this.steps.toArray()[this.activeStepIndex].stepForm;
  }

  get activeStepIndex(): number {
    return this._activeStepIndex;
  }

  @Input() set activeStepIndex(activeStepIndex: number) {
    this._activeStepIndex = activeStepIndex;
    this.toggleStepsVisibility(activeStepIndex);
    this.setUpdatedFormData();
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.isLoading = true;
  }

  ngAfterContentInit(): void {
    this.steps.toArray().forEach((step: MultiStepFormStep) => {
      step.stepForm.patchValue(this.entity);
      this.masterForm.push(step.stepForm);
    });

    // Include summary in wizard steps
    if (this.summary) {
      // Since ng-content is nested in a formGroup, a step form must be initialized
      this.summary.stepForm = this.fb.group({});
      this.wizardSteps.reset([...this.steps, this.summary]);
    } else {
      this.wizardSteps = this.steps;
    }

    setTimeout(() => {
      // Set activeStepIndex to the last step (summary) on edit mode, else set on first step
      this.activeStepIndex = this.isEditMode ? this.wizardSteps.length - 1 : 0;
      this.isLoading = false;
    });
  }

  /**
   * Toggle steps (<ng-content>'s children) visibility in DOM
   * @param activeStepIndex
   * @returns
   */
  toggleStepsVisibility(activeStepIndex: number): void {
    if (!this.stepFormEl) return;
    const stepsEl: HTMLElement[] = this.stepFormEl.nativeElement.children;
    [...stepsEl].forEach((stepEl: HTMLElement, index: number) => {
      stepEl.hidden = index === activeStepIndex ? false : true;
    });
  }

  cancel(): void {
    this.onCancel.emit();
  }

  prev(): void {
    this.activeStepIndex = this.activeStepIndex - 1;
  }

  next(): void {
    this.activeStepIndex = this.activeStepIndex + 1;
  }

  goToStep(activeStepIndex: number): void {
    this.activeStepIndex = activeStepIndex;
  }

  setUpdatedFormData(): void {
    this.formData = this.masterForm.reduce(
      (masterForm, stepForm) => ({ ...masterForm, ...stepForm.getRawValue() }),
      {}
    );
    this._formDataSubject.next(this.formData);
  }

  submitForm(): void {
    this.onFormSubmit.emit(this.formData);
  }

  trackByFn(index: number): number {
    return index;
  }
}
